import { useEffect, useState } from "react";

/**
 * Loads the Google Maps JS API on demand, and tells the caller when it is
 * ready.
 *
 * WHY: the script used to sit in public/index.html as a plain synchronous
 * <script>, so it blocked parsing on EVERY page - including the blog, the
 * area pages, the price list and the 404, none of which render a map.
 *
 * WHY A READY FLAG rather than just adding `async`: twelve places build
 * marker icons with `new window.google.maps.Size(...)` during render. With a
 * synchronous script that was always safe. Make the script async without
 * gating and those throw on first paint, which unmounts the tree and gives a
 * blank page. Callers must render their map only once this returns true.
 *
 * The promise is module-level, so several components mounting at once share
 * a single script tag rather than racing to append their own.
 */

// Public by necessity - the Maps JS API key ships in any client bundle. It
// was previously hardcoded in public/index.html; moving it here is what lets
// the script load on demand. Restrict it by HTTP referrer in Google Cloud
// Console, not by hiding it.
const MAPS_KEY = "AIzaSyDSUxZ31U_kNs_9WGOiZ_9RMhqDO8Xi_AQ";
const SRC = "https://maps.googleapis.com/maps/api/js?key=" + MAPS_KEY + "&libraries=places";

let loadPromise = null;

function isReady() {
    return typeof window !== "undefined" && !!(window.google && window.google.maps);
}

function loadMaps() {
    if (isReady()) return Promise.resolve();
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
        // If a script tag already exists (an older cached index.html, say),
        // wait on it rather than adding a second one - loading the Maps API
        // twice logs a warning and can re-initialise it.
        const existing = document.querySelector('script[data-google-maps="1"]');
        if (existing) {
            existing.addEventListener("load", resolve);
            existing.addEventListener("error", reject);
            return;
        }

        const script = document.createElement("script");
        script.src = SRC;
        script.async = true;
        script.defer = true;
        script.setAttribute("data-google-maps", "1");
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return loadPromise;
}

/*
 * `shouldLoad` lets a caller defer the download - the home page search passes
 * false until its map is near the viewport (see useNearViewport). It still
 * returns true straight away if another component has already loaded the API.
 */
export default function useGoogleMaps(shouldLoad = true) {
    const [ready, setReady] = useState(isReady);

    useEffect(() => {
        if (ready || !shouldLoad) return undefined;

        let cancelled = false;
        loadMaps()
            .then(() => {
                if (!cancelled) setReady(true);
            })
            .catch(() => {
                // Leave `ready` false. The caller renders no map rather than
                // crashing, and the rest of the page is unaffected.
                if (!cancelled) setReady(false);
            });

        return () => {
            cancelled = true;
        };
    }, [ready, shouldLoad]);

    return ready;
}
